import { environment } from "@config";
import { StorageKey, UserQuery } from "@ctypes";
import { AnalyticsBrowser } from "@segment/analytics-next";
import { AppsFlyer } from "./AppsFlyer";
import { Facebook } from "./Facebook";
import { SessionReplay } from "./SessionReplay";
import { TagManager } from "./TagManager";

const instance = new AnalyticsBrowser();

const load = async () => {
  // Not awaited - the Mixpanel SDK loads in its own chunk and bindDeviceId
  // waits on it internally, so Segment is never held up behind that fetch.
  SessionReplay.init();

  // Mixpanel receives events through Segment, not the Mixpanel SDK, so the
  // replay id has to be attached to the payload on its way out - that is what
  // ties an event to the replay it happened in. Registered before load so it
  // is in place for the first page call.
  instance.addSourceMiddleware(({ payload, next }) => {
    const { type } = payload.obj;

    if (type === "track" || type === "page") {
      payload.obj.properties = {
        ...payload.obj.properties,
        ...SessionReplay.getReplayProperties(),
      };
    }

    next(payload);
  });

  instance.load({ writeKey: environment.segment.key });

  const user = await instance.user();
  const anonymousId = user.anonymousId();

  if (anonymousId) {
    localStorage.setItem(StorageKey.AnonymousId, anonymousId.toString());
    SessionReplay.bindDeviceId(anonymousId.toString());
  }
};

const identify = (user?: UserQuery["user"]) => {
  const userId = user?.id;

  if (userId) {
    Facebook.identify(userId);
    AppsFlyer.identify(userId);
    SessionReplay.identify(userId);
    return instance.identify(userId);
  }
};

// `tagManagerProperties` reaches the dataLayer only - it is never handed to
// AppsFlyer, Meta or Segment. That is what it is for: the ad-conversion tags in
// GTM want the user's email and phone to match a conversion back to someone who
// saw an ad, and Meta in particular forbids raw PII in custom event parameters,
// so those values must not ride along on the ordinary `properties` fan-out.
const track = (
  eventName: string,
  properties?: Record<string, any>,
  tagManagerProperties?: Record<string, any>,
) => {
  AppsFlyer.track(eventName, properties);
  Facebook.track(eventName, properties);
  TagManager.track(eventName, { ...properties, ...tagManagerProperties });
  return instance.track(eventName, properties);
};

// `route` is the screen name the event is reported under — normally a Routes
// key via getRouteForPath, or an explicit override for screens the app has
// forked into several routes but the user experiences as one.
const page = (route: string | null, properties?: Record<string, any>) => {
  if (!route) return;

  AppsFlyer.track(`${route} Viewed`, properties);
  Facebook.track(`${route} Viewed`, properties);
  TagManager.track(`${route} Viewed`, properties);
  return instance.page(route, properties);
};

export const Analytics = { load, identify, track, page };
